import React, { useRef } from 'react';
import { Button } from '@mui/material';
import PopoverComponent, { PopoverComponentRef } from 'src/components/Popover';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import toast from 'react-hot-toast';
import { confirmWrapper } from 'src/components/Dialog';
import { SeperatorIcon } from 'src/icons';
import RHFInput from 'src/components/elements/Input/RHFInput';
import { Confirmation } from 'src/components';
import * as yup from 'yup';
import { DialogContentProps } from 'src/components/Dialog/Dialog';
import { JobTitleFormSubmitHandler, JobTitleFormValues } from '../../types';

interface AddJobTitleProps extends DialogContentProps {
  defaultValues?: JobTitleFormValues;
  disabled: boolean;
  readOnly?: boolean;
  onCreateJobTitle: (data: JobTitleFormSubmitHandler) => void;
}

const validationSchema = yup.object().shape({
  value: yup.string().typeError('Enter Job Title').required('Job Title required'),
});

const AddJobTitle = function AddActivity(props: AddJobTitleProps) {
  const {
    defaultValues,
    hideDialogLoading,
    showDialogLoading,
    close,
    onCreateJobTitle,
  } = props;

  const approveRef = useRef<PopoverComponentRef>(null);
  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<JobTitleFormValues>({
    defaultValues,
    resolver,
  });

  const {
    handleSubmit, reset,
    formState: { isDirty },
  } = methods;
  const { id } = defaultValues || {};
  const showLoading = (message: string) => {
    if (showDialogLoading) {
      showDialogLoading(message);
    }
  };

  const hideLoading = () => {
    if (hideDialogLoading) {
      hideDialogLoading();
    }
  };

  const submitHandler: SubmitHandler<JobTitleFormValues> = async () => {
    if (Object.keys(methods.formState.dirtyFields).length > 0) {
      if (approveRef.current?.open) {
        approveRef.current?.open();
      }
    } else {
      toast('No changes', {
        position: 'bottom-center',
      });
    }
  };

  const handleCreate = async () => {
    const data = methods.getValues();
    try {
      if (Object.keys(methods.formState.dirtyFields).length > 0) {
        const loadingMessage = id ? 'Updating Job Title...' : 'Creating New Job Title ...';
        showLoading(loadingMessage);
        const userFormObj: JobTitleFormSubmitHandler = {
          values: data,
          formInstance: methods,
        };
        await onCreateJobTitle(userFormObj);
        if (close) {
          close();
          const message = id ? 'Job Title updated successfully!' : 'Job Title created successfully!';
          await confirmWrapper(message, {
            hideSecondaryBtn: true,
            hidePrimaryBtn: true,
            variant: 'success',
            autoClose: true,
          });
        }
      } else {
        toast('No changes', {
          position: 'bottom-center',
        });
      }
    } catch (e) {
      console.info('...');
      // close confirmation popover
      if (approveRef.current?.close) approveRef.current?.close();
    } finally {
      hideLoading();
    }
  };

  const confirmationMessage = id ? 'Are you sure you want to update the job title?' : 'Are you sure you want to add new job title?';

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className="flex justify-center items-center w-full h-[60px] py-10">
          <SeperatorIcon stopColor="#D9D9D9" className="rotate-90" width={90} />
          <div className="flex justify-center text-[#28364C] font-medium text-base pl-[13px]">
            {id ? 'Edit Candidate Job Title' : 'Add New Candidate Job Title'}
          </div>
          <SeperatorIcon stopColor="#D9D9D9" className="rotate-90" width={90} />
        </div>

        <div className="flex items-start justify-center px-[10%] gap-7 bg-[#FAFAFA] py-[5%]">
          <div className="w-[180px]">
            <RHFInput name="value" control={methods.control} label="Job Title Name" />
          </div>
        </div>

        <div className="flex justify-center items-center py-[20px] gap-4">
          <Button
            className="w-[114px]"
            variant="outlined"
            disabled={!isDirty}
            onClick={() => reset({})}
            data-testid="add-activity/reset"
          >
            Reset
          </Button>
          <PopoverComponent
            ref={approveRef}
            controlled
            Component={(
              <Confirmation
                variant="warning"
                text={confirmationMessage}
                confirm={handleCreate}
              />
            )}
          >
            <Button
              className="w-[114px]"
              type="submit"
              color="primary"
              variant="contained"
              data-testid="add-activity/add-or-update"
            >
              {id ? 'Update' : 'Add'}
            </Button>
          </PopoverComponent>
        </div>
      </form>
    </FormProvider>
  );
};

AddJobTitle.defaultProps = {
  readOnly: false,
  defaultValues: undefined,
};

AddJobTitle.propTypes = {

};

export default AddJobTitle;
